import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Loader2, Users2, TrendingUp, ChevronDown, ChevronRight, Euro, Download, RefreshCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function AdminCommissions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [expandAll, setExpandAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates/commissions/breakdown`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement commissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    const newState = !expandAll;
    setExpandAll(newState);
    const next = {};
    (data?.affiliates || []).forEach(a => { next[a.affiliate_id] = newState; });
    setExpanded(next);
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="admin-commissions-panel">
      {/* Grand totals header */}
      <Card className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <Euro className="w-6 h-6 text-emerald-400" />
          <div>
            <div className="text-lg font-bold">Commissions à rétrocéder</div>
            <div className="text-xs text-muted-foreground">Vue consolidée par lien d&apos;affiliation · par user · par mois · total depuis inception</div>
          </div>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
            </Button>
            <Button size="sm" variant="outline" onClick={toggleAll} data-testid="commissions-toggle-all">
              {expandAll ? <ChevronDown className="w-3.5 h-3.5 mr-1" /> : <ChevronRight className="w-3.5 h-3.5 mr-1" />}
              {expandAll ? 'Tout replier' : 'Tout déplier'}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded border border-white/10 bg-white/5">
            <div className="text-xs text-muted-foreground uppercase">Total tous statuts</div>
            <div className="text-2xl font-bold text-emerald-400">{eur(data.grand_total_all_time_eur)}</div>
          </div>
          <div className="p-3 rounded border border-amber-500/20 bg-amber-500/5">
            <div className="text-xs text-muted-foreground uppercase">En attente (rétractation J+15)</div>
            <div className="text-xl font-bold text-amber-400">{eur(data.grand_total_pending_eur)}</div>
          </div>
          <div className="p-3 rounded border border-blue-500/20 bg-blue-500/5">
            <div className="text-xs text-muted-foreground uppercase">Approuvées (à payer)</div>
            <div className="text-xl font-bold text-blue-400">{eur(data.grand_total_approved_eur)}</div>
          </div>
          <div className="p-3 rounded border border-emerald-500/20 bg-emerald-500/5">
            <div className="text-xs text-muted-foreground uppercase">Déjà payées</div>
            <div className="text-xl font-bold text-emerald-400">{eur(data.grand_total_paid_eur)}</div>
          </div>
        </div>
      </Card>

      {data.affiliates.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">Aucun affilié pour l&apos;instant.</Card>
      ) : data.affiliates.map(aff => {
        const isOpen = expanded[aff.affiliate_id];
        return (
          <Card key={aff.affiliate_id} className="overflow-hidden" data-testid={`commissions-affiliate-${aff.affiliate_code}`}>
            {/* Header row (always visible) */}
            <button
              className="w-full text-left p-4 flex flex-wrap items-center gap-3 hover:bg-white/5"
              onClick={() => toggle(aff.affiliate_id)}
              data-testid={`commissions-toggle-${aff.affiliate_code}`}
            >
              {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              <div className="flex-1 min-w-[160px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-emerald-400 font-bold text-sm">{aff.affiliate_code}</span>
                  <span className="text-sm font-medium">{aff.affiliate_name}</span>
                  {!aff.affiliate_active && <span className="text-xs px-1.5 py-0.5 rounded bg-white/10 text-muted-foreground">Inactif</span>}
                </div>
                <div className="text-xs text-muted-foreground">{aff.affiliate_email}</div>
              </div>
              <div className="flex gap-3 flex-wrap text-xs">
                <div className="text-right">
                  <div className="text-muted-foreground">Clics</div>
                  <div className="font-mono">{aff.clicks_total}</div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground">Filleuls</div>
                  <div className="font-mono">{aff.unique_referrals}</div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground">Conv.</div>
                  <div className="font-mono">{aff.conversion_rate_pct}%</div>
                </div>
                <div className="text-right border-l border-white/10 pl-3">
                  <div className="text-muted-foreground">En attente</div>
                  <div className="font-mono text-amber-400">{eur(aff.totals_by_status_eur.pending)}</div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground">Approuvées</div>
                  <div className="font-mono text-blue-400">{eur(aff.totals_by_status_eur.approved)}</div>
                </div>
                <div className="text-right">
                  <div className="text-muted-foreground">Payées</div>
                  <div className="font-mono text-emerald-400">{eur(aff.totals_by_status_eur.paid)}</div>
                </div>
                <div className="text-right border-l border-white/10 pl-3">
                  <div className="text-muted-foreground">TOTAL</div>
                  <div className="font-mono text-emerald-300 font-bold text-sm">{eur(aff.total_all_time_eur)}</div>
                </div>
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-white/10 p-4 space-y-5 bg-white/[0.02]">
                {/* Monthly chart */}
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" /> Par mois
                    <span className="text-xs text-muted-foreground font-normal">
                      ({aff.monthly.length} mois avec activité)
                    </span>
                  </div>
                  {aff.monthly.length === 0 ? (
                    <div className="text-xs text-muted-foreground p-4">Aucune commission enregistrée pour l&apos;instant.</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={aff.monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 10 }} />
                          <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{ background: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                            formatter={(v) => eur(v)}
                          />
                          <Bar dataKey="amount_eur" fill="#10b981" radius={[3, 3, 0, 0]} name="Commission (€)" />
                        </BarChart>
                      </ResponsiveContainer>
                      <table className="w-full text-xs mt-2">
                        <thead className="text-muted-foreground border-b border-white/10">
                          <tr>
                            <th className="text-left p-2">Mois</th>
                            <th className="text-right p-2">Conversions</th>
                            <th className="text-right p-2">Filleuls uniques</th>
                            <th className="text-right p-2">Commission</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aff.monthly.map(m => (
                            <tr key={m.month} className="border-b border-white/5">
                              <td className="p-2 font-mono">{m.month}</td>
                              <td className="p-2 text-right font-mono">{m.conversions}</td>
                              <td className="p-2 text-right font-mono">{m.unique_users}</td>
                              <td className="p-2 text-right font-mono text-emerald-400">{eur(m.amount_eur)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>

                {/* Per-user breakdown */}
                <div>
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Users2 className="w-4 h-4 text-emerald-400" /> Par utilisateur
                    <span className="text-xs text-muted-foreground font-normal">
                      ({aff.by_user.length} filleul{aff.by_user.length > 1 ? 's' : ''})
                    </span>
                  </div>
                  {aff.by_user.length === 0 ? (
                    <div className="text-xs text-muted-foreground p-4">Aucun filleul payant pour l&apos;instant.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-muted-foreground border-b border-white/10">
                        <tr>
                          <th className="text-left p-2">Email filleul</th>
                          <th className="text-right p-2">Conversions</th>
                          <th className="text-left p-2">1ère</th>
                          <th className="text-left p-2">Dernière</th>
                          <th className="text-right p-2">Total commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aff.by_user.map(u => (
                          <tr key={u.user_id} className="border-b border-white/5">
                            <td className="p-2">{u.user_email || <span className="text-muted-foreground">—</span>}</td>
                            <td className="p-2 text-right font-mono">{u.conversions}</td>
                            <td className="p-2 text-muted-foreground">{u.first_conversion_at?.substring(0, 10)}</td>
                            <td className="p-2 text-muted-foreground">{u.last_conversion_at?.substring(0, 10)}</td>
                            <td className="p-2 text-right font-mono text-emerald-400 font-bold">{eur(u.total_eur)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </Card>
        );
      })}

      <Card className="p-4 border-amber-500/20 bg-amber-500/5 text-xs text-muted-foreground">
        <strong className="text-amber-300">Rappel de la grille</strong> — 15 % one-shot sur les abonnements mensuels · 20 % sur packs 3 mois · 25 % sur annuel · 15 % sur packs analyses.
        Les commissions apparaissent en <span className="text-amber-400">en attente</span> pendant J+15 (rétractation), puis passent en <span className="text-blue-400">approuvées</span> et enfin <span className="text-emerald-400">payées</span> après export CSV + virement.
      </Card>
    </div>
  );
}
